import { Stack, Title, Text, Button, Group, Badge, Alert, ThemeIcon, Paper } from '@mantine/core';
import {
  IconArrowLeft,
  IconCheck,
  IconCalendar,
  IconAlertCircle,
  IconChefHat,
  IconWand,
  IconArrowsShuffle,
  IconListCheck,
  IconCalendarCog,
  IconPencil,
  IconClock,
} from '@tabler/icons-react';
import { MealPlanPreferences, PlanningMode } from '../../../types/mealPlan';
import { effectiveRotationSize } from '../../../utils/rotationSize';
import { useFeatureFlag } from '../../../hooks/useFeatureFlags';

export type WizardMeta = {
  mode: PlanningMode | null;
  noveltyDays: number;
  lockedCount: number;
};

type ReviewCreateStepProps = {
  preferences: MealPlanPreferences;
  dateRange: [string | null, string | null];
  onCreatePlan: () => void;
  onBack: () => void;
  loading: boolean;
  wizardMeta: WizardMeta;
};

export const ReviewCreateStep = ({
  preferences,
  dateRange,
  onCreatePlan,
  onBack,
  loading,
  wizardMeta,
}: ReviewCreateStepProps) => {
  const [startDate, endDate] = dateRange;

  const getTotalDays = () => {
    if (!startDate || !endDate) return 0;
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    return Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  const formatDateRange = () => {
    if (!startDate || !endDate) return 'No dates selected';
    const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
    const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const formatDateForDisplay = (date: Date) => `${date.getMonth() + 1}/${date.getDate()}`;
    return `${formatDateForDisplay(start)} - ${formatDateForDisplay(end)} (${getTotalDays()} days)`;
  };

  const totalDays = getTotalDays();
  const additionalMealsEnabled = useFeatureFlag('additional_meals');
  const penciledMeals = preferences.penciledMeals ?? [];
  // Days NOT in quickMealDays are take-your-time; undefined means all-quick
  const takeYourTimeDays = (() => {
    const quick = preferences.quickMealDays;
    if (quick === undefined || !startDate) return [] as string[];
    const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const [year, month, day] = startDate.split('-').map(Number);
    const ordered: string[] = [];
    for (let i = 0; i < Math.min(totalDays, 7); i++) {
      const date = new Date(year, month - 1, day + i);
      ordered.push(DAYS[date.getDay()]);
    }
    return ordered.filter((d) => !quick.includes(d));
  })();
  const mealsNeeded =
    preferences.uniqueMeals ?? Math.max(0, totalDays - penciledMeals.length);
  const fullyPenciled = totalDays > 0 && penciledMeals.length >= totalDays;
  // Penciled nights aren't cooked from recipes; meal prep covers the rest
  const cookingDays = Math.max(0, totalDays - penciledMeals.length);
  // Whether the flag-on "How we'll build it" card has any detail rows;
  // without this the empty detail stack would still claim its top margin
  const hasBuildDetails =
    (!fullyPenciled && preferences.uniqueMeals !== undefined && preferences.uniqueMeals < cookingDays) ||
    !!preferences.additionalMeals?.breakfast ||
    !!preferences.additionalMeals?.lunch ||
    (!fullyPenciled && takeYourTimeDays.length > 0) ||
    penciledMeals.length > 0;

  // Colors follow the app's source-composition language (see
  // MealPlanPreferencesDrawer): violet = new creations, teal = my recipes,
  // blue = mixed
  const getModeSummary = (): { icon: React.ReactNode; label: string; detail: string; color: string } => {
    switch (wizardMeta.mode) {
      case 'shuffle_cookbook':
        return {
          icon: <IconArrowsShuffle size={16} />,
          label: 'Shuffle my recipes',
          color: 'teal',
          detail:
            wizardMeta.noveltyDays > 0
              ? `A rotation from your cookbook, plus ${wizardMeta.noveltyDays} new idea${
                  wizardMeta.noveltyDays !== 1 ? 's' : ''
                } sprinkled in`
              : 'A rotation from your cookbook, no surprises',
        };
      case 'pick_a_few':
        return {
          icon: <IconListCheck size={16} />,
          label: 'Your picks',
          color: 'blue',
          detail:
            wizardMeta.lockedCount > 0
              ? `${wizardMeta.lockedCount} meal${wizardMeta.lockedCount !== 1 ? 's' : ''} you picked${
                  mealsNeeded - wizardMeta.lockedCount > 0
                    ? `. We'll fill the other ${mealsNeeded - wizardMeta.lockedCount}`
                    : ''
                }`
              : "We'll pick every meal for you",
        };
      case 'surprise_me':
      default:
        return {
          icon: <IconWand size={16} />,
          label: 'Surprise me',
          color: 'violet',
          detail: "We'll pick every meal for you, tailored to your family's tastes",
        };
    }
  };

  // A fully penciled week generates nothing; the mode is irrelevant and its
  // usual copy ("We'll pick every meal for you") would be false
  const modeSummary = fullyPenciled
    ? {
        icon: <IconPencil size={16} />,
        label: 'Penciled in by you',
        color: 'grape',
        detail: 'Every night is covered by your own meals. Nothing for us to plan.',
      }
    : getModeSummary();
  const canCreate = startDate && endDate;

  const hasExtras =
    !!preferences.customRequests ||
    (preferences.events?.length ?? 0) > 0 ||
    !!preferences.usePantryIngredients;

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      {/* Header */}
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconCheck size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Review & create
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            Here's what we'll build. You can still shape everything after it's created.
          </Text>
        </Stack>
      </Stack>

      {/* Planning Period */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%',
        }}
      >
        <Group gap="md" align="flex-start">
          <ThemeIcon size={32} radius="md" color="orange" variant="light">
            <IconCalendar size={18} />
          </ThemeIcon>
          <Stack gap="xs" style={{ flex: 1 }}>
            <Text fw={600} size="lg" c="dark">
              Planning period
            </Text>
            <Text size="md">{formatDateRange()}</Text>
          </Stack>
        </Group>
      </Paper>

      {/* How we'll build it */}
      <Paper
        radius="lg"
        p={{ base: 'lg', sm: 'xl' }}
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%',
        }}
      >
        <Group gap="md" align="flex-start">
          <ThemeIcon size={32} radius="md" color={modeSummary.color} variant="light">
            {modeSummary.icon}
          </ThemeIcon>
          <Stack gap="xs" style={{ flex: 1 }}>
            <Text fw={600} size="lg" c="dark">
              How we'll build it
            </Text>
            <Text size="md" fw={600} c={`${modeSummary.color}.7`}>
              {modeSummary.label}
            </Text>
            <Text size="sm" c="dimmed">
              {modeSummary.detail}
            </Text>
            {/* Flag ON: one uniform detail list, dimmed labels and a single
                gap, matching the extras card. The per-row icon colors read
                as a rainbow once breakfast/lunch rows joined. */}
            {additionalMealsEnabled ? (
              hasBuildDetails && (
              <Stack gap={6} mt="xs">
                {!fullyPenciled && preferences.uniqueMeals !== undefined && preferences.uniqueMeals < cookingDays && (
                  <>
                    <Group gap="xs">
                      <Text size="sm" fw={500} c="dimmed">
                        Batch cooking:
                      </Text>
                      <Text size="sm">
                        {preferences.uniqueMeals} dinner{preferences.uniqueMeals !== 1 ? 's' : ''} covering {cookingDays} night{cookingDays !== 1 ? 's' : ''}
                      </Text>
                    </Group>
                    {preferences.mealPrepPreferences?.generalPreferences && (
                      <Text size="sm" style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }} pl="md">
                        "{preferences.mealPrepPreferences.generalPreferences}"
                      </Text>
                    )}
                  </>
                )}
                {preferences.additionalMeals?.breakfast && (
                  <Group gap="xs">
                    <Text size="sm" fw={500} c="dimmed">
                      Breakfasts:
                    </Text>
                    <Text size="sm">
                      {(() => {
                        const n = effectiveRotationSize(
                          'breakfast',
                          totalDays,
                          preferences.additionalMeals?.breakfast?.rotationSize
                        );
                        return `${n} recipe${n !== 1 ? 's' : ''} on rotation covering all ${totalDays} days`;
                      })()}
                    </Text>
                  </Group>
                )}
                {preferences.additionalMeals?.lunch && (
                  <Group gap="xs">
                    <Text size="sm" fw={500} c="dimmed">
                      Lunches:
                    </Text>
                    <Text size="sm">
                      {preferences.additionalMeals.lunch.mode === 'leftovers'
                        ? 'dinner leftovers cover the next day'
                        : (() => {
                            const n = effectiveRotationSize(
                              'lunch',
                              totalDays,
                              preferences.additionalMeals?.lunch?.rotationSize
                            );
                            return `${n} recipe${n !== 1 ? 's' : ''} on rotation covering all ${totalDays} days`;
                          })()}
                    </Text>
                  </Group>
                )}
                {!fullyPenciled && takeYourTimeDays.length > 0 && (
                  <Group gap="xs">
                    <Text size="sm" fw={500} c="dimmed">
                      Take-your-time nights:
                    </Text>
                    <Text size="sm">{takeYourTimeDays.join(', ')}</Text>
                  </Group>
                )}
                {penciledMeals.length > 0 && (
                  <>
                    <Text size="sm" fw={500} c="dimmed">
                      Your nights:
                    </Text>
                    {penciledMeals.map((meal) => (
                      <Text key={meal.day} size="sm" pl="md">
                        {meal.day}: {meal.name}
                      </Text>
                    ))}
                  </>
                )}
              </Stack>
              )
            ) : (
              <>
                {!fullyPenciled && preferences.uniqueMeals !== undefined && preferences.uniqueMeals < cookingDays && (
                  <Stack gap="xs" mt="xs">
                    <Group gap="xs">
                      <Group gap={4}>
                        <IconChefHat size={14} color="var(--mantine-color-blue-7)" />
                        <Text size="sm" fw={500} c="blue.7">
                          Meal prep:
                        </Text>
                      </Group>
                      <Text size="sm">
                        {preferences.uniqueMeals} meal{preferences.uniqueMeals !== 1 ? 's' : ''} for {cookingDays} day{cookingDays !== 1 ? 's' : ''}
                      </Text>
                    </Group>
                    {preferences.mealPrepPreferences?.generalPreferences && (
                      <Text size="sm" style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }} pl="md">
                        "{preferences.mealPrepPreferences.generalPreferences}"
                      </Text>
                    )}
                  </Stack>
                )}
                {/* Take-your-time days, restored: the wizard rebuild (c1d1c7a)
                    dropped this from the old Meal Planning Options section */}
                {!fullyPenciled && takeYourTimeDays.length > 0 && (
                  <Group gap="xs" mt="xs">
                    <Group gap={4}>
                      <IconClock size={14} color="var(--mantine-color-green-7)" />
                      <Text size="sm" fw={500} c="green.7">
                        Take your time:
                      </Text>
                    </Group>
                    <Text size="sm">{takeYourTimeDays.join(', ')}</Text>
                  </Group>
                )}
                {penciledMeals.length > 0 && (
                  <Stack gap={4} mt="xs">
                    <Group gap={4}>
                      <IconPencil size={14} color="var(--mantine-color-grape-7)" />
                      <Text size="sm" fw={500} c="grape.7">
                        Your nights:
                      </Text>
                    </Group>
                    {penciledMeals.map((meal) => (
                      <Text key={meal.day} size="sm" pl="md">
                        {meal.day}: {meal.name}
                      </Text>
                    ))}
                  </Stack>
                )}
              </>
            )}
          </Stack>
        </Group>
      </Paper>

      {/* This week's extras (read-only summary; hidden when nothing is set) */}
      {hasExtras && (
        <Paper
          radius="lg"
          p={{ base: 'lg', sm: 'xl' }}
          style={{
            border: '1px solid var(--mantine-color-gray-2)',
            width: '100%',
          }}
        >
          <Group gap="md" align="flex-start">
            <ThemeIcon size={32} radius="md" color="green" variant="light">
              <IconCalendarCog size={18} />
            </ThemeIcon>
            <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
              <Text fw={600} size="lg" c="dark">
                This week's extras
              </Text>

              {preferences.customRequests && (
                <Stack gap={2}>
                  <Text size="sm" fw={500} c="dimmed">
                    Preferences & requests
                  </Text>
                  <Text size="sm" style={{ fontStyle: 'italic', whiteSpace: 'pre-wrap' }}>
                    "{preferences.customRequests}"
                  </Text>
                </Stack>
              )}

              {(preferences.events?.length ?? 0) > 0 && (
                <Stack gap={4}>
                  <Text size="sm" fw={500} c="dimmed">
                    Guests & occasions
                  </Text>
                  {preferences.events!.map((event, index) => (
                    <Group key={index} gap="xs" align="flex-start" wrap="nowrap">
                      <Badge variant="light" color="indigo" size="sm" style={{ flexShrink: 0 }}>
                        {event.day}
                      </Badge>
                      <Text size="sm" style={{ minWidth: 0 }}>
                        {[
                          event.occasion,
                          event.guestCount
                            ? `${event.guestCount} guest${event.guestCount === 1 ? '' : 's'}`
                            : undefined,
                          event.specialRequests ? `"${event.specialRequests}"` : undefined,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              )}

              {preferences.usePantryIngredients && (
                <Text size="sm" c="green.7">
                  ✓ Using your pantry items when planning meals
                </Text>
              )}
            </Stack>
          </Group>
        </Paper>
      )}

      {!canCreate && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
          Please select a date range to create your meal plan.
        </Alert>
      )}

      {/* Creation Info */}
      <Paper
        p="lg"
        radius="lg"
        bg="gray.0"
        style={{
          border: '1px solid var(--mantine-color-gray-2)',
          width: '100%',
        }}
      >
        <Group gap="md" align="center">
          <ThemeIcon size={40} radius="md" color="blue" variant="light">
            <IconCheck size={20} />
          </ThemeIcon>

          <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
            <Text fw={600} c="blue.7" style={{ wordBreak: 'break-word' }}>
              Ready to go?
            </Text>
            <Text size="sm" c="dimmed" style={{ wordBreak: 'break-word' }}>
              We'll create a personalized meal plan based on your preferences. This usually takes 2-3 minutes.
            </Text>
            <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
              Your plan is a starting point. You can swap any meal after it's created.
            </Text>
          </Stack>
        </Group>
      </Paper>

      {/* Navigation */}
      <Group justify="space-between" pt="md" wrap="nowrap" className="wizard-footer">
        <Button
          variant="subtle"
          size="md"
          leftSection={<IconArrowLeft size={18} />}
          onClick={onBack}
          disabled={loading}
          color="orange"
        >
          Back
        </Button>

        <Button
          size="lg"
          leftSection={<IconCheck size={20} />}
          onClick={onCreatePlan}
          loading={loading}
          disabled={!canCreate}
          color="green"
          style={{ minWidth: 'fit-content', whiteSpace: 'nowrap' }}
        >
          {loading ? 'Creating...' : 'Create plan'}
        </Button>
      </Group>
    </Stack>
  );
};
